import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Inbox, RefreshCw } from "lucide-react";

// First-class loading / empty / error states. Defined once, reused everywhere — never a
// blank screen, a spinner-only flash, or a raw error string.

export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("space-y-3", className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Card key={i} className="p-4 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-24" />
        </Card>
      ))}
    </div>
  );
}

export function KpiLoadingState({ tiles = 4 }: { tiles?: number }) {
  return (
    <div className="flex flex-wrap gap-3" aria-busy="true" aria-label="Loading">
      {Array.from({ length: tiles }).map((_, i) => (
        <Card key={i} className="p-4 min-w-[9rem] flex-1 space-y-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-24" />
        </Card>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  icon,
  action,
  tone = "neutral",
  className,
}: {
  title: string;
  body?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  tone?: "neutral" | "good";
  className?: string;
}) {
  return (
    <Card className={cn("p-8 flex flex-col items-center text-center gap-2", className)}>
      <div className={cn("mb-1", tone === "good" ? "text-primary" : "text-muted-foreground")}>
        {icon ?? <Inbox className="h-7 w-7" aria-hidden />}
      </div>
      <h3 className="font-heading text-base font-medium text-foreground">{title}</h3>
      {body && <p className="font-body text-sm text-muted-foreground max-w-sm">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </Card>
  );
}

export function ErrorState({
  onRetry,
  title = "We couldn't load this just now.",
  className,
}: {
  onRetry?: () => void;
  title?: string;
  className?: string;
}) {
  return (
    <Card className={cn("p-8 flex flex-col items-center text-center gap-3", className)}>
      <p className="font-body text-sm text-muted-foreground max-w-sm">{title}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden /> Try again
        </Button>
      )}
    </Card>
  );
}
